import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import { MemberTypeClient, MemberTypeDTO } from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberTypeClient(API_BASE_URL, axiosInstance);

// Query hook - Get all membertypes for dropdowns (cached 4 hours)
export const useMemberTypes = () =>
  useQuery({
    queryKey: ['membertypes'],
    queryFn: async () => {
      const response = await apiClient.searchMemberTypes({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Direct API search (no caching) - for admin tables with server-side paging
export const useMemberTypeSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchMemberTypes({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single memberType by ID
export const useMemberTypeById = (id: string) =>
  useQuery({
    queryKey: ['membertypes', id],
    queryFn: async () => {
      const response = await apiClient.getMemberTypeById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });

// Mutation hook - Create memberType
export const useCreateMemberType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: MemberTypeDTO) => {
      await apiClient.createMemberType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membertypes'] });
    },
  });
};

// Mutation hook - Update memberType
export const useUpdateMemberType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: MemberTypeDTO }) => {
      await apiClient.updateMemberType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membertypes'] });
    },
  });
};

// Mutation hook - Delete memberType
export const useDeleteMemberType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteMemberType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membertypes'] });
    },
  });
};
